import { createContext, useEffect, useState, useCallback } from "react";
import supabase from "../Config/Supabase";
import db from "../local database/localDB";

const DataContext = createContext();

export function DataProvider({ children }) {
  const [Zones, setZones] = useState([]);
  const [ZonesC, setZonesC] = useState([]);
  const [Villages, setVillages] = useState([]);
  const [farmerlist, setFarmerList] = useState([]);
  const [Payment, setPayments] = useState([]);
  const [vegetableBundle, setVegetableBundle] = useState([]);
  const [farmerbundle, setFarmerBundle] = useState([]);
  const [Objective, setObjective] = useState([]);
  const [Objectives, setObjectives] = useState([]);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [syncStatus, setSyncStatus] = useState({
    zones: "idle",
    villages: "idle",
    payments: "idle",
    farmerBundle: "idle",
    vegetableBundle: "idle",
    zonesC: "idle",
    objective: "idle",
    objectives: "idle",
    farmerList: "idle",
  });

  // Network status monitoring
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      // Attempt to sync when back online
      syncAllData();
    };

    const handleOffline = () => setIsOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  // Initial data load
  useEffect(() => {
    loadInitialData();
  }, []);

  // Load data from local DB first, then sync with server
  const loadInitialData = async () => {
    try {
      // Load all data from local DB first for immediate UI rendering
      await Promise.all([
        loadLocalData("zone", setZones),
        loadLocalData("village", setVillages),
        loadLocalData("zone_nutritional_coordinator", setZonesC),
        loadLocalData("payments", setPayments),
        loadLocalData("vegetable_bundle", setVegetableBundle),
        loadLocalData("farmer_bundle", setFarmerBundle),
        loadLocalData("objective", setObjective),
        loadLocalData("objective_ludewa", setObjectives),
        loadLocalData("farmer_list", setFarmerList),
      ]);

      // Then sync with server if online
      if (isOnline) {
        syncAllData();
      }
    } catch (error) {
      console.error("Error loading initial data:", error);
    }
  };

  // Generic function to load data from local DB
  const loadLocalData = async (tableName, setState) => {
    try {
      const data = await db[tableName].toArray();
      if (data.length > 0) {
        setState(data);
      }
    } catch (error) {
      console.warn(`Error loading ${tableName} from local DB:`, error);
    }
  };

  // Generic function to sync data with server and update local DB
  const syncData = async (tableName, setState, orderBy = "created_at") => {
    const statusKey = getStatusKey(tableName);
    setSyncStatus((prev) => ({ ...prev, [statusKey]: "syncing" }));

    try {
      if (tableName === "payments" || tableName === "farmer_bundle") {
        await syncLargeTable(tableName, setState, orderBy);
      } else {
        await syncSimpleTable(tableName, setState);
      }

      setSyncStatus((prev) => ({ ...prev, [statusKey]: "success" }));
    } catch (error) {
      console.warn(`Error syncing ${tableName}:`, error.message);
      setSyncStatus((prev) => ({ ...prev, [statusKey]: "error" }));

      // Load from local DB as fallback
      await loadLocalData(tableName, setState);
    }
  };

  // Sync simple tables (non-paginated)
  const syncSimpleTable = async (tableName, setState) => {
    const { data, error } = await supabase.from(tableName).select();
    if (error) throw new Error(error.message);

    setState(data || []);
    await db[tableName].clear();
    if (data && data.length > 0) {
      await db[tableName].bulkAdd(data);
    }
  };

  // Sync large tables with pagination
  const syncLargeTable = async (tableName, setState, orderBy) => {
    let allData = [];
    let start = 0;
    const batchSize = 1000;
    let hasMore = true;

    while (hasMore) {
      const { data, error } = await supabase
        .from(tableName)
        .select("*")
        .order(orderBy, { ascending: false })
        .range(start, start + batchSize - 1);

      if (error) throw new Error(error.message);

      if (Array.isArray(data) && data.length > 0) {
        allData = [...allData, ...data];
      }

      if (!data || data.length < batchSize) {
        hasMore = false;
      } else {
        start += batchSize;
      }
    }

    setState(allData);
    await db[tableName].clear();
    if (allData.length > 0) {
      await db[tableName].bulkAdd(allData);
    }
  };

  // Get status key for sync status tracking
  const getStatusKey = (tableName) => {
    const keyMap = {
      zone: "zones",
      village: "villages",
      payments: "payments",
      farmer_bundle: "farmerBundle",
      vegetable_bundle: "vegetableBundle",
      zone_nutritional_coordinator: "zonesC",
      objective: "objective",
      objective_ludewa: "objectives",
    };
    return keyMap[tableName] || tableName;
  };

  // Sync all data
  const syncAllData = useCallback(async () => {
    if (!isOnline) return;

    try {
      await Promise.allSettled([
        syncData("zone", setZones),
        syncData("village", setVillages),
        syncData("zone_nutritional_coordinator", setZonesC),
        syncData("payments", setPayments),
        syncData("vegetable_bundle", setVegetableBundle),
        syncData("farmer_bundle", setFarmerBundle),
        syncData("objective", setObjective),
        syncData("objective_ludewa", setObjectives),
        syncData("farmer_list", setFarmerList),
      ]);

      setLastSyncTime(new Date());
    } catch (error) {
      console.error("Error during full sync:", error);
    }
  }, [isOnline]);

  // Individual fetch functions with offline support
  const fetchFarmerBundle = useCallback(async () => {
    await syncData("farmer_bundle", setFarmerBundle);
  }, []);

  const fetchPayments = useCallback(async () => {
    await syncData("payments", setPayments);
  }, []);

  const fetchFarmerList = useCallback(async () => {
    await syncData("farmer_list", setFarmerList);
  }, []);

  const fetchZones = useCallback(async () => {
    await syncData("zone", setZones);
  }, []);

  const fetchVillage = useCallback(async () => {
    await syncData("village", setVillages);
  }, []);

  const fetchVegetablebundle = useCallback(async () => {
    await syncData("vegetable_bundle", setVegetableBundle);
  }, []);

  const fetchZoneConnect = useCallback(async () => {
    await syncData("zone_nutritional_coordinator", setZonesC);
  }, []);

  const fetchObjective = useCallback(async () => {
    await syncData("objective", setObjective);
  }, []);

  const fetchObjectives = useCallback(async () => {
    await syncData("objective_ludewa", setObjectives);
  }, []);

  // Clear all local data
  const clearLocalData = async () => {
    try {
      await Promise.all([
        db.zone.clear(),
        db.village.clear(),
        db.zone_nutritional_coordinator.clear(),
        db.payments.clear(),
        db.vegetable_bundle.clear(),
        db.farmer_bundle.clear(),
        db.objective.clear(),
        db.objective_ludewa.clear(),
        db.farmer_list.clear(),
      ]);
      console.log("Local data cleared successfully");
    } catch (error) {
      console.error("Error clearing local data:", error);
    }
  };

  // Force refresh data (clear local and fetch fresh)
  const forceRefresh = async () => {
    if (!isOnline) {
      console.warn("Cannot force refresh while offline");
      return;
    }

    await clearLocalData();
    await syncAllData();
  };

  // Get data freshness info
  const getDataInfo = () => ({
    isOnline,
    lastSyncTime,
    syncStatus,
    totalRecords: {
      zones: Zones.length,
      villages: Villages.length,
      payments: Payment.length,
      farmerBundle: farmerbundle.length,
      vegetableBundle: vegetableBundle.length,
      zonesC: ZonesC.length,
      objective: Objective.length,
      objectives: Objectives.length,
      farmerList: farmerlist.length,
    },
  });

  return (
    <DataContext.Provider
      value={{
        // Data states
        Zones,
        Payment,
        farmerbundle,
        vegetableBundle,
        Villages,
        Objective,
        Objectives,
        ZonesC,
        farmerlist,

        // Individual setters (for backward compatibility)
        setFarmerBundle,

        // Fetch functions
        fetchFarmerBundle,
        fetchPayments,
        fetchZones,
        fetchVillage,
        fetchVegetablebundle,
        fetchZoneConnect,
        fetchObjective,
        fetchObjectives,
        fetchFarmerList,

        // Utility functions
        syncAllData,
        forceRefresh,
        clearLocalData,

        // Status and info
        isOnline,
        syncStatus,
        lastSyncTime,
        getDataInfo,
      }}
    >
      {children}
    </DataContext.Provider>
  );
}

export default DataContext;
